import { EventEmitter, Injectable } from '@angular/core';

import { LayerApi } from '../api/layer-api';
import { MapLayerUsageApi } from '../api/map-layer-usage-api';
import { IDistinctValue, IExtractedQuery, ILayerColumn, IMapcoreFeatureLayer, IQuery, ITable, ITableColumn, IWorkingQuery } from '../types';

import { GisToolsService } from './gis-tools.service';
import { LayerService } from './layer.service';
import { ToSqlStringService } from './to-sql-string.service';
const maxBetweenValues = 2;
const twoCharacterFields = ['cty_code', 'district_no', 'county_number', 'district_number', 'county_code', 'county_no', 'cty_number', 'cty_no'];

@Injectable({
    providedIn: 'root'
})

export class FilterService {
    public readonly applyFilter = new EventEmitter<any>();

    constructor(private readonly layerApi: LayerApi, private readonly toSqlStringService: ToSqlStringService, private readonly layerService: LayerService, private readonly gisToolsService: GisToolsService, private readonly mapLayerUsageApi: MapLayerUsageApi) { }

    async getLayerData(layerId: number) {
        if (layerId) {
            return await this.layerApi.getLayers(
                [layerId],
                'name systemQueryJSON queryJSON tables{name geometryType owner} columns(orderBy:"alias"){alias, name, databaseDataType, separatorCharacter, domainValueBasedFilter} tables { metaTable { id }}');
        }
    }

    populateWorkingQueryFromQuery(query: IQuery, layerColumns: (ILayerColumn | ITableColumn)[]) {
        if (!query) {
            throw new Error('Query cannot be null');
        }
        let deepSubqueries: IQuery[];
        if (query.subqueries) {
            deepSubqueries = query.subqueries.filter(sq => sq.subqueries && sq.subqueries.length);
            query.subqueries = query.subqueries.filter(sq => !(sq.subqueries && sq.subqueries.length));
        }
        const cloneOfQuery = this.toSqlStringService.getSimplifiedDeepCopyOfQuery(query, false);
        const workingQuery: IWorkingQuery = this.getEmptyQuery();
        if (cloneOfQuery.operator !== 'OR' && cloneOfQuery.operator !== 'AND') {
            if (cloneOfQuery.field) {
                workingQuery.subqueries.push(cloneOfQuery);
            }
            workingQuery.operator = 'AND';
        } else {
            workingQuery.operator = cloneOfQuery.operator;
            workingQuery.subqueries = [].concat(cloneOfQuery.subqueries);
            workingQuery.subqueries = workingQuery.subqueries.filter(x => x);
        }
        for (const subquery of workingQuery.subqueries) {
            if (subquery.field) {
                subquery.field = subquery.field.toLowerCase();
            }
            if (!subquery.operatorOptions) {
                subquery.operatorOptions = this.getOperatorOptionsForSubquery(workingQuery.subqueries[0], layerColumns);
            }
            if (subquery.operator && subquery.operator.toLowerCase() === 'between') {
                const queryValues = subquery.value.split(',');
                if (queryValues.length === 2) {
                    subquery.value = `${this.toSqlStringService.replaceAnyVariables(queryValues[0])}, ${this.toSqlStringService.replaceAnyVariables(queryValues[1])}`;
                }
            }
            if (this.toSqlStringService.hasAnyVariables(subquery.value)) {
                subquery.value = this.toSqlStringService.replaceAnyVariables(subquery.value, true);
            }
        }
        if (workingQuery.subqueries.length === 0) {
            workingQuery.subqueries.push({ field: '', operator: '=', value: null, operatorOptions: this.getOperatorOptionsForSubquery({ field: '', operator: '=', value: null }, null) });
        }
        this.generateChildRecordsQuery(query, workingQuery, layerColumns);
        this.generateJoinTableQuery(query, workingQuery, layerColumns);
        if (deepSubqueries && deepSubqueries.length) {
            query.subqueries.push(...deepSubqueries);
            workingQuery.subqueries.push(...deepSubqueries.map(dsq => ({ ...dsq })));
        }

        if (workingQuery.subqueries && workingQuery.subqueries.length && !workingQuery.subqueries[0].distinctValues) {
            //debugger;
        }

        return workingQuery;
    }

    private generateJoinTableQuery(query: IQuery, workingQuery: IWorkingQuery, layerColumns: (ILayerColumn | ITableColumn)[]) {
        if (query && query.joinRecord) {
            workingQuery.joinRecord = query.joinRecord;
            workingQuery.primaryLayerRMSKeys = query.primaryLayerRMSKeys;
        }
        if (query) {
            const joinTableQuery = this.toSqlStringService.getSimplifiedDeepCopyOfQuery(query.joinTableQuery, false);
            workingQuery.joinTableQuery = { subqueries: [{ field: '', operator: '=', value: null }] };
            if (joinTableQuery.operator !== 'OR' && joinTableQuery.operator !== 'AND') {
                if (joinTableQuery.field && joinTableQuery.field !== '') {
                    workingQuery.joinTableQuery.subqueries = [joinTableQuery];
                }
                workingQuery.joinTableQuery.operator = 'AND';
            } else {
                workingQuery.joinTableQuery.operator = joinTableQuery.operator;
                workingQuery.joinTableQuery.subqueries = [].concat(joinTableQuery.subqueries);
            }
            for (const subquery of workingQuery.joinTableQuery.subqueries) {
                subquery.operatorOptions = this.getOperatorOptionsForSubquery(subquery, layerColumns);
            }
        }
    }

    private generateChildRecordsQuery(query: IQuery, workingQuery: IWorkingQuery, layerColumns: (ILayerColumn | ITableColumn)[]) {
        if (query && query.childTableRecord) {
            workingQuery.childTableRecord = query.childTableRecord;
        }
        if (query) {
            const childTableQuery = this.toSqlStringService.getSimplifiedDeepCopyOfQuery(query.childTableQuery, false);
            workingQuery.childTableQuery = { subqueries: [{ field: '', operator: '=', value: null }] };
            if (childTableQuery.operator !== 'AND' && childTableQuery.operator !== 'OR') {
                if (childTableQuery.field && childTableQuery.field !== '') {
                    workingQuery.childTableQuery.subqueries = [childTableQuery];
                }
                workingQuery.childTableQuery.operator = 'AND';
            } else {
                workingQuery.childTableQuery.operator = childTableQuery.operator;
                workingQuery.childTableQuery.subqueries = [].concat(childTableQuery.subqueries);
            }
            for (const subquery of workingQuery.childTableQuery.subqueries) {
                subquery.operatorOptions = this.getOperatorOptionsForSubquery(subquery, layerColumns);
            }
        }
    }

    /* tslint:disable:cyclomatic-complexity */
    public async populateDistinctValues(layerId: number, subquery: IWorkingQuery, layerQuery: IQuery, workingQuery: IWorkingQuery, layerColumns: (ILayerColumn | ITableColumn)[], subqueryIndex?: number, isTable?: boolean, forcePetl?: boolean) {
        if (layerId && subquery && subquery.field) {
            subquery.loadingDistinct = true;
            workingQuery.subqueries = this.populateWorkingSubqueryDisplayProperties(workingQuery.subqueries, layerColumns);
            subquery.distinctValues = null;
            subquery.distinctValueColumnName = subquery.field;
            const petlProjectIdClause = this.toSqlStringService.getPetlProjectIdClauseIfPresent(layerId, workingQuery, forcePetl);

            const additionalQueryForDistinctValues = { operator: layerQuery && layerQuery.operator === 'OR' ? 'OR' : 'AND', subqueries: [] } as IQuery;
            for (const sq of workingQuery.subqueries.filter(query => query.subqueries && query.subqueries.length)) {
                additionalQueryForDistinctValues.subqueries.push(this.toSqlStringService.getSimplifiedDeepCopyOfQuery(sq));
            }
            if (layerQuery && layerQuery.field && layerQuery.value) {
                additionalQueryForDistinctValues.subqueries.push(this.toSqlStringService.getSimplifiedDeepCopyOfQuery(layerQuery));
            } else if (layerQuery && layerQuery.subqueries && layerQuery.subqueries.length && layerQuery.subqueries.length > 0) {
                for (const lq of layerQuery.subqueries) {
                    additionalQueryForDistinctValues.subqueries.push(this.toSqlStringService.getSimplifiedDeepCopyOfQuery(lq));
                }
            }
            if (petlProjectIdClause) {
                this.toSqlStringService.addFilter(additionalQueryForDistinctValues, petlProjectIdClause);
            }
            //finds current layer column and sets separator character
            const layerColumn = layerColumns.find(x => x.name.toLowerCase().trim() === subquery.field.toLowerCase().trim());
            if (layerColumn && layerColumn.separatorCharacter) {
                subquery.separatorCharacter = layerColumn.separatorCharacter;
            }
            // Check that distinct on previous is set
            if (subquery.filterDistinctOnPrevious) {
                let queryMap: IQuery = {
                    operator: workingQuery.operator,
                    subqueries: []
                };
                // iterate through previous subqueries to the one selected
                for (const [mapIndex, x] of workingQuery.subqueries.filter(q => !(q.subqueries && q.subqueries.length)).entries()) {
                    if (mapIndex < subqueryIndex) {
                        // if previous fields have a value then include them in filter
                        // if operator is 'NULL' or 'NOT NULL', it will also add to the query
                        if (x.isMultiple) {
                            queryMap.subqueries.push({ field: x.field, operator: x.operator, isMultiple: x.isMultiple, multiValue: x.multiValue });
                        } else if ((x.value && x.value.length > 0) || (x.operator && x.operator.indexOf('NULL') >= 0)) {
                            queryMap.subqueries.push({ field: x.field, operator: x.operator, value: x.value });
                        } else {
                            // if a previous subquery doesn't have value then it prevents filtering
                            // turn flag back off and break loop
                            subquery.filterDistinctOnPrevious = false;
                            break;
                        }
                    }
                }
                // if flag is still set then filter values
                if (subquery.filterDistinctOnPrevious) {
                    if (additionalQueryForDistinctValues.operator === 'OR' && additionalQueryForDistinctValues.subqueries.length) {
                        const orQuery: IQuery = {
                            operator: additionalQueryForDistinctValues.operator,
                            subqueries: additionalQueryForDistinctValues.subqueries
                        };
                        queryMap.subqueries.push(orQuery);
                        subquery.distinctValues = this.mapDistinctValues(subquery, layerColumns, await this.getDistictValues(layerId, subquery.field, isTable, queryMap));
                    } else {
                        additionalQueryForDistinctValues.subqueries.forEach(aqfdv => queryMap = this.toSqlStringService.addQueryClauseIfNotPresent(queryMap, aqfdv));
                        subquery.distinctValues = this.mapDistinctValues(subquery, layerColumns, await this.getDistictValues(layerId, subquery.field, isTable, queryMap));
                    }
                }
            } else if (subquery.previousFilterDistinctOnPreviousValue !== subquery.filterDistinctOnPrevious || !subquery.distinctValues) {
                const currentColumn = layerColumns.find(column => column.name === subquery.field);

                if (currentColumn) {
                    if (currentColumn.domainValueBasedFilter === 'T') {
                        //holds values we get back in format of text: ... value: ...
                        const domainValuesShortDesc: IDistinctValue[] = [];

                        //gets column data and fills array
                        currentColumn.domainValues.forEach(value => {
                            domainValuesShortDesc.push({ text: value.shortDescription, value: value.shortDescription });
                        });

                        //sorts alphabetically by text displayed to user
                        domainValuesShortDesc.sort((a, b) => a.text.localeCompare(b.text));

                        //sets values to variable that is returned
                        subquery.distinctValues = domainValuesShortDesc;
                    } else {
                        subquery.distinctValues = this.mapDistinctValues(subquery, [currentColumn], await this.getDistictValues(layerId, subquery.field, isTable, additionalQueryForDistinctValues));
                    }
                }
            }

            if (subquery.distinctValues && subquery.distinctValues.length && subquery.distinctValues.length > 0) {
                subquery.filteredDistinctValues = this.sortSubQuery(subquery.distinctValues);
            }

            subquery.filteredDistinctValues = this.sortIfDate(subquery.filteredDistinctValues, layerColumns, subquery.field);
            subquery.customValues = [];

            if (subquery.value != null) {
                if (this.showMultiple(subquery.operator)) {
                    if (!subquery.isMultiple && !subquery.multiValue) {
                        subquery.isMultiple = true;
                        subquery.multiValue = subquery.value.split(',').filter(v => v.trim() !== '');
                    }

                    for (const val of subquery.multiValue) {
                        if (subquery.filteredDistinctValues && !subquery.filteredDistinctValues.map(x => x.value.toLowerCase()).includes(val.toLowerCase())) {
                            subquery.filteredDistinctValues.push({ value: val, text: val });

                            if (!subquery.customValues.map(cv => cv.toLowerCase()).includes(val.toLowerCase())) {
                                subquery.customValues.push(val);
                            }
                        }
                    }
                } else if (subquery.value.indexOf(',') === -1 && subquery.filteredDistinctValues && !subquery.filteredDistinctValues.map(fdv => fdv.value && fdv.value.toLowerCase()).includes(subquery.value.toLowerCase())) {
                    subquery.filteredDistinctValues.push({ value: subquery.value, text: subquery.value });

                    if (!subquery.customValues.map(cv => cv.toLowerCase()).includes(subquery.value.toLowerCase())) {
                        subquery.customValues.push(subquery.value);
                    }
                }
            }

            subquery.loadingDistinct = false;
        }
        return subquery;
    }

    populateWorkingSubqueryDisplayProperties(workingSubqueries: IWorkingQuery[], layerColumns: (ILayerColumn | ITableColumn)[]) {
        let subQueries: IWorkingQuery[] = [];
        if (workingSubqueries && workingSubqueries.length) {
            for (const subquery of workingSubqueries) {
                const workingSubquery = subquery as IWorkingQuery;
                if (!workingSubquery.field || workingSubquery.distinctValueColumnName !== workingSubquery.field) {
                    workingSubquery.distinctValueColumnName = undefined;
                    workingSubquery.distinctValues = undefined;
                }

                workingSubquery.isMultiple = this.showMultiple(subquery.operator);
                workingSubquery.disableDistinctValues = !subquery.field;
                workingSubquery.disableValueEntry = !subquery.operator || subquery.operator.indexOf('NULL') >= 0;
                workingSubquery.inputType = this.getInputTypeForSubQuery(subquery, layerColumns);
                workingSubquery.operatorOptions = this.getOperatorOptionsForSubquery(subquery, layerColumns);

                if (workingSubquery.isMultiple) {
                    if (workingSubquery.multiValue) {
                        workingSubquery.value = workingSubquery.multiValue.toString();
                    } else if (workingSubquery.value !== null) {
                        workingSubquery.multiValue = workingSubquery.value.split(',').map(v => v.trim());
                    }
                } else {
                    if (!workingSubquery.value || workingSubquery.value === '') {
                        workingSubquery.multiValue = [];
                    } else {
                        workingSubquery.multiValue = workingSubquery.value.split(',').map(v => v.trim());
                    }
                }

                if (subquery.separatorCharacter) {
                    workingSubquery.separatorCharacter = subquery.separatorCharacter;
                }

                subQueries.push(workingSubquery);
            }
            subQueries = workingSubqueries;
        } else {
            workingSubqueries = [{ field: '', operator: '=', value: null }];
            workingSubqueries[0].operatorOptions = this.getOperatorOptionsForSubquery(workingSubqueries[0], layerColumns);
        }
        return workingSubqueries;
    }

    public async getDistictValues(Id, columnName: string, isTable?: boolean, query?: IQuery) {
        let distinctValues: string[] = [];
        try {
            if (isTable) {
                distinctValues = await this.layerApi.getDistinctTableValues(Id, columnName);
            } else {
                distinctValues = await this.layerApi.getDistinctValues(Id, columnName, this.toSqlStringService.getSimplifiedDeepCopyOfQuery(query));
            }
            if (distinctValues) {
                return distinctValues;
            }
        } catch { }
        return [];
    }
    public clearValuesIfNeeded(subqueries: IWorkingQuery[]) {
        if (subqueries) {
            for (const subquery of subqueries) {
                if (subquery.operator && subquery.operator.includes('NULL')) {
                    subquery.value = null;
                }
                if (subquery.operator && subquery.operator.includes('BETWEEN') && subquery.multiValue) {
                    subquery.multiValue = subquery.multiValue.slice(-(maxBetweenValues));
                }
            }
        }
    }

    public offSetIsNumber(offsetEndColName: string, layerColumns: any[]) {
        if (offsetEndColName !== null) {
            for (const column of layerColumns) {
                if (column.name.toLowerCase() === offsetEndColName.toLowerCase()) {
                    return ((column.dataType && ['number', 'float', 'long'].indexOf(column.dataType.toLowerCase()) > -1) ? true : false || (column.databaseDataType && ['number', 'float', 'long'].indexOf(column.databaseDataType.toLowerCase()) > -1) ? true : false);
                }
            }
        }
        return false;

    }

    public getFinalizedQuery(workingQuery: IWorkingQuery) {
        for (let i = 0, l = workingQuery.subqueries.filter(sq => sq.field && sq.value).length; i < l - 1; i++) {
            if ((workingQuery.subqueries[i].value.length === 1) && (twoCharacterFields.indexOf(workingQuery.subqueries[i].field) >= 0)) {
                workingQuery.subqueries[i].value = '0' + workingQuery.subqueries[i].value;
            }
        }
        for (let i = 0, l = workingQuery.childTableQuery.subqueries.length; i < l - 1; i++) {
            if ((workingQuery.childTableQuery.subqueries[i].value.length === 1) && (twoCharacterFields.indexOf(workingQuery.childTableQuery.subqueries[i].field) >= 0)) {
                workingQuery.childTableQuery.subqueries[i].value = '0' + workingQuery.childTableQuery.subqueries[i].value;
            }
        }
        for (let i = 0, l = workingQuery.joinTableQuery.subqueries.length; i < l - 1; i++) {
            if ((workingQuery.joinTableQuery.subqueries[i].value.length === 1) && (twoCharacterFields.indexOf(workingQuery.joinTableQuery.subqueries[i].field) >= 0)) {
                workingQuery.joinTableQuery.subqueries[i].value = '0' + workingQuery.joinTableQuery.subqueries[i].value;
            }
        }
        const query = this.toSqlStringService.getSimplifiedDeepCopyOfQuery(workingQuery, false);
        query.value = this.toSqlStringService.replaceAnyVariables(query.value);
        query.childTableQuery = this.toSqlStringService.getSimplifiedDeepCopyOfQuery(workingQuery.childTableQuery, false);
        query.childTableRecord = workingQuery.childTableRecord;
        query.joinTableQuery = this.toSqlStringService.getSimplifiedDeepCopyOfQuery(workingQuery.joinTableQuery, false);
        query.joinRecord = workingQuery.joinRecord;
        query.primaryLayerRMSKeys = workingQuery.primaryLayerRMSKeys;
        return query;
    }
    getInputTypeForSubQuery(query: IQuery, layerColumns: (ILayerColumn | ITableColumn)[]) {
        let databaseType = this.getDatabaseTypeForSubQuery(query, layerColumns);
        if (databaseType) {
            databaseType = databaseType.toLowerCase();
        }
        if (query.operator &&
            (query.operator.indexOf('IN') >= 0
                || (query.operator.indexOf('BETWEEN') >= 0 && databaseType !== 'date')
                || query.operator.indexOf('INCLUDES') >= 0
                || query.operator === 'GUID')) {
            return 'text';
        }
        if (query.operator && query.operator.indexOf('NULL') >= 0) {
            return 'null';
        }
        switch (databaseType) {
            case 'number':
            case 'float':
                return 'number';
            case 'date':
                return 'date';
            default:
                return 'text';
        }
    }

    getDatabaseTypeForSubQuery(subquery: IQuery, layerColumns: (ILayerColumn | ITableColumn)[]) {
        let databaseType = null;
        if (subquery.field && layerColumns && layerColumns.length > 0) {
            const layerColumn = layerColumns.find(x => x.name.toLowerCase().trim() === subquery.field.toLowerCase().trim());
            if (layerColumn) {
                databaseType = isLayerColumn(layerColumn) ? layerColumn.databaseDataType : layerColumn.dataType;
            }
        }
        return databaseType;
    }

    getOperatorOptionsForSubquery(subquery: IQuery, layerColumns: (ILayerColumn | ITableColumn)[]) {
        const databaseType = this.getDatabaseTypeForSubQuery(subquery, layerColumns);
        return this.toSqlStringService.getOperatorOptionsForDatatype(databaseType);
    }

    showMultiple(operator: string) {
        return operator === 'IN' || operator === 'NOT IN' || operator === 'INCLUDES' || operator === 'NOT INCLUDES' || operator === 'BETWEEN' || operator === 'NOT BETWEEN';
    }
    getEmptyQuery() {
        return { operator: 'AND', subqueries: [], joinTableQuery: { subqueries: [], operator: 'AND' }, childTableQuery: { subqueries: [], operator: 'AND' } };
    }
    async populateWorkingSql(layerQuery: IQuery, workingQuery: IWorkingQuery, baseTable: ITable, layerColumns: ILayerColumn[]) {
        const rootQuery: IQuery = { operator: 'AND', subqueries: [] };
        rootQuery.subqueries.push(layerQuery);
        rootQuery.subqueries.push(workingQuery);
        return this.toSqlStringService.getSQLFromQuery(rootQuery, baseTable, layerColumns);
    }

    populateSummaryFromQuery(workingQuery: IQuery, layerColumns: ILayerColumn[], joinColumns: ILayerColumn[], childColumns: ILayerColumn[]) {
        let summaryItemsString = '';
        let basicSummaryString = '';
        let joinSummaryString = '';
        let childRecordsSummaryString = '';
        const stringList = [];
        workingQuery.value = this.toSqlStringService.replaceAnyVariables(workingQuery.value);
        basicSummaryString = this.toSqlStringService.getSummaryStringForQuery(workingQuery, layerColumns);
        if (basicSummaryString !== '') {
            stringList.push(basicSummaryString);
        }
        joinSummaryString = this.toSqlStringService.getJoinFilterSummaryFromQuery(workingQuery, joinColumns);
        if (joinSummaryString !== '') {
            stringList.push(joinSummaryString);
        }
        childRecordsSummaryString = this.toSqlStringService.getChildFilterSummaryFromQuery(workingQuery, childColumns);
        if (childRecordsSummaryString !== '') {
            stringList.push(childRecordsSummaryString);
        }
        summaryItemsString = stringList.join(' and ');
        if (summaryItemsString !== '' && summaryItemsString.indexOf('when') === -1) {
            summaryItemsString = `when ${summaryItemsString}`;
        }
        return summaryItemsString;
    }

    getSummaryQuery(workingQuery: IQuery, layerColumns: ILayerColumn[], joinColumns: ILayerColumn[], childColumns: ILayerColumn[]) {
        let summaryItemsString = '';
        let basicSummaryString = '';
        let joinSummaryString = '';
        let childRecordsSummaryString = '';
        const stringList = [];
        basicSummaryString = this.toSqlStringService.parseIQueryToString(workingQuery, layerColumns);
        if (basicSummaryString !== '') {
            stringList.push(basicSummaryString);
        }
        joinSummaryString = this.toSqlStringService.getJoinFilterSummaryFromQuery(workingQuery, joinColumns);
        if (joinSummaryString !== '') {
            stringList.push(joinSummaryString);
        }
        childRecordsSummaryString = this.toSqlStringService.getChildFilterSummaryFromQuery(workingQuery, childColumns);
        if (childRecordsSummaryString !== '') {
            stringList.push(childRecordsSummaryString);
        }
        summaryItemsString = stringList.join(' and ');
        return summaryItemsString;
    }

    private sortIfDate(vals: any[], layerColumns: any[], field: string): any[] {
        const result = layerColumns.filter(x => x.name.toUpperCase() === field.toUpperCase());
        if (result.length > 0 && ((result[0].databaseDataType && result[0].databaseDataType.toUpperCase() === 'DATE') || (result[0].dataType && result[0].dataType.toUpperCase() === 'DATE'))) {
            const dateList: any[] = [];
            vals.sort(function(a, b) {
                return Number(new Date(a.text)) - Number(new Date(b.text));
            });
            vals.forEach(val => {
                const date = new Date(val.text);
                const newDate = val.text.toUpperCase() === 'NULL' ? val.text : `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
                const value = val.value.toUpperCase() === 'NULL' ? val.value : `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
                if (!dateList.filter(x => x.value === value).length) {
                    dateList.push({ text: newDate, value });
                }
            });
            vals = dateList;
        }
        return vals;
    }
    private sortSubQuery(s: any[]): any[] {
        let returnType = typeof (s[0]);
        if (returnType === 'string') {
            const reg = /^\d+$/;
            returnType = reg.test(s[0]) ? 'number' : 'string';
        }
        let isSorted = true;
        //see if values need sorted
        if (returnType === 'number') {
            for (let i = 0; i < s.length - 1; i++) {
                const a = parseInt(s[i], 10);
                const b = parseInt(s[i + 1], 10);
                if (a > b) {
                    isSorted = false;
                    break;
                }
            }
        } else if (returnType === 'string') {
            for (let i = 0; i < s.length - 1; i++) {
                const a = s[i];
                const b = s[i + 1];
                if (a.localeCompare(b) > 0) {
                    isSorted = false;
                    break;
                }
            }
        }

        //sort if needed
        if (!isSorted && returnType === 'number') {
            return s.sort((a, b) => {
                const numA: number = parseInt(a, 10);
                const numB: number = parseInt(b, 10);
                if (numA < numB) {
                    return -1;
                }
                if (numA > numB) {
                    return 1;
                }
                return 0;
            });
        } else if (!isSorted && returnType === 'string') {
            return s.sort((a, b) => {
                const comparison = a.localeCompare(b);
                if (comparison < 0) {
                    return -1;
                } else if (comparison > 0) {
                    return 1;
                }
                return 0;
            });
        } else {
            return s;
        }
    }
    public async saveLayerUsage(layerId, query: IQuery) {
        const filters = this.extractQueryInfo(query);
        const districts = await this.getDistrictsFromFilters(filters);
        if (districts && districts.length && districts.length > 0) {
            districts.forEach(district => {
                this.mapLayerUsageApi.saveLayerUsage(layerId, JSON.stringify(filters), district);
            });
        }
    }

    public extractQueryInfo(query: IQuery, results: IExtractedQuery[] = []): IExtractedQuery[] {
        if (query.field && query.operator) {
            const info: IExtractedQuery = {
                field: query.field,
                operator: query.operator
            };
            if (query.primaryLayerRMSKeys) {
                info.primaryLayerRMSKeys = query.primaryLayerRMSKeys;
            }
            if (query.isMultiple && query.multiValue) {
                info.multiValue = query.multiValue;
            } else if (query.value) {
                info.value = query.value;
            }
            results.push(info);
        }

        if (query.subqueries && query.subqueries.length > 0) {
            for (const subquery of query.subqueries) {
                this.extractQueryInfo(subquery, results);
            }
        }
        return results;
    }

    public async getDistrictsFromFilters(filters: IExtractedQuery[]) {
        const districts: number[] = [];

        for (const filter of filters) {
            if (filter.field.toLowerCase().indexOf('district') > -1) {
                const district = parseInt(filter.value, 0);
                if (!districts.includes(district)) {
                    districts.push(district);
                }
            }

            const isCountyRelated = filter.primaryLayerRMSKeys && filter.primaryLayerRMSKeys.ctyCodeColName === filter.field ||
                filter.field.toLowerCase().indexOf('cty_code') > -1 ||
                filter.field.toLowerCase().indexOf('county') > -1;

            if (isCountyRelated) {
                let counties;

                if (filter.multiValue && filter.multiValue.length > 0) {
                    counties = await this.gisToolsService.getCounties(filter.multiValue.map(c => parseInt(c, 0)));
                } else if (filter.value) {
                    counties = await this.gisToolsService.getCounties([parseInt(filter.value, 0)]);
                }

                if (counties && counties.length > 0) {
                    counties.forEach(county => {
                        const district = parseInt(county.districtNumber, 0);
                        if (!districts.includes(district)) {
                            districts.push(district);
                        }
                    });
                }
            }
        }
        return districts;
    }

    async populateExternalDistinctValues(externalLayerId: string, subquery: IWorkingQuery, workingQuery: IWorkingQuery, workingSql: string, subqueryIndex: number): Promise<IWorkingQuery> {

        const esriLayer = this.layerService.getEsriLayerByUniqueId(externalLayerId) as IMapcoreFeatureLayer;

        if (!subquery.field) {
            return subquery;
        }

        subquery.loadingDistinct = true;
        subquery.distinctValueColumnName = subquery.field;
        const prop: string = subquery.field.toUpperCase();

        const query = esriLayer.createQuery();
        if (subquery.filterDistinctOnPrevious) {
            if (subqueryIndex === 0) {
                //first subquery can't filter by previous
                query.where = '1=1';
            } else {
                //get a substring of workingSql from the startIndex up to the current subquery's clause.
                //For most common cases, this check won't matter (will use full sql length).
                //However, in the situation, for instance, where a user has 3 subqueries and edits the 2nd, then this will
                //  correctly only use the 1st clause, and not the 1st and 3rd to get the 2nds distinct values.
                const joinOperator: string = workingQuery.operator;
                let endIndex: number = this.nthOccurrence(workingSql, joinOperator, subqueryIndex);
                if (endIndex === -1) {
                    //didn't find an extra instance of joinOperator, so use full sql
                    endIndex = workingSql.length;
                }
                query.where = workingSql.substring(0, endIndex);
            }
        } else {
            query.where = '1=1';
        }
        query.spatialRelationship = 'intersects';
        query.outFields = [prop];
        const stats: any = {
            statisticType: 'count',
            onStatisticField: prop
        };
        query.outStatistics = [stats];
        query.groupByFieldsForStatistics = [prop];

        const retData = await esriLayer.queryFeatures(query);

        const distValues: string[] = [];
        if (retData) {
            for (const feature of retData.features) {
                let val = feature.attributes[prop];
                if (!val && feature.attributes.Expr1) {
                    val = feature.attributes.Expr1.toString();
                }
                distValues.push(val);
            }
            subquery.distinctValues = [...distValues].map(dv => ({ text: dv, value: dv }));
            subquery.filteredDistinctValues = this.sortSubQuery(subquery.distinctValues);
        }
        subquery.loadingDistinct = false;

        return subquery;
    }

    private nthOccurrence(s1: string, s2: string, n: number) {
        let temp = s1;
        let tempIndex = -1;
        let finalIndex = 0;
        for (let occurence = 0; occurence < n; occurence += 1) {
            tempIndex = temp.indexOf(s2);
            if (tempIndex === -1) {
                finalIndex = 0;
                break;
            }
            temp = temp.substring(tempIndex += 1);
            finalIndex += tempIndex;
        }
        return finalIndex -= 1;
    }

    private mapDistinctValues(subquery: IWorkingQuery, columns: (ILayerColumn | ITableColumn)[], distinctValues: string[]) {
        let retVal: IDistinctValue[] = [];
        if (distinctValues) {
            retVal = distinctValues.map(dv => ({ text: dv, value: dv }));
            if (subquery && subquery.field && columns) {
                const matchingColumn = columns.find(c => c.name.toLowerCase().trim() === subquery.field.toLowerCase().trim());
                if (matchingColumn && matchingColumn.domainValues) {
                    for (const v of retVal) {
                        const matchingValue = matchingColumn.domainValues.find(dv => (subquery.inputType && subquery.inputType === 'number' ? parseFloat(dv.codeValue.toString()) : dv.codeValue).toString() === v.value);
                        if (matchingValue) {
                            v.text = `${matchingValue.codeValue} - ${matchingValue.shortDescription}`;
                        }
                    }
                }
            }
        }
        return retVal;
    }

    onApplyFilter(esriLayer: any) {
        this.applyFilter.emit(esriLayer);
    }
}

function isLayerColumn(layerColumn: (ILayerColumn | ITableColumn)): layerColumn is ILayerColumn {
    return Object.keys(layerColumn).includes('databaseDataType');
}
